import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { Table, type Column } from '../components/ui/Table';
import { useAuth } from '../context/AuthContext';
import type { SessionListItem } from '../types';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

const columns: Column<SessionListItem>[] = [
  {
    header: 'Start',
    cell: (row) => (
      <Link to={`/sessions/${row.id}`} className="text-brand-600 hover:underline dark:text-brand-400">
        {new Date(row.startedAt).toLocaleString('de-DE')}
      </Link>
    ),
  },
  { header: 'Einstieg', cell: (row) => <span className="font-mono text-xs">{row.entryPage}</span> },
  { header: 'Ausstieg', cell: (row) => <span className="font-mono text-xs">{row.exitPage ?? '—'}</span> },
  { header: 'Dauer', cell: (row) => formatDuration(row.durationSeconds), align: 'right' },
  { header: 'Gerät', cell: (row) => row.deviceClass },
  { header: 'Browser', cell: (row) => row.browser ?? '—' },
  { header: 'Referrer', cell: (row) => row.referrer ?? 'Direkt' },
];

export function Sessions() {
  const { currentSiteId } = useAuth();

  const to = new Date().toISOString();
  const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const { data, isLoading } = useQuery({
    queryKey: ['sessions', currentSiteId, from.slice(0, 10), to.slice(0, 10)],
    queryFn: () => api.listSessions(currentSiteId!, from, to, 100, 0),
    enabled: !!currentSiteId,
  });

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Sessions (letzte 7 Tage)</h1>
      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <Table columns={columns} rows={data?.sessions ?? []} emptyLabel="Keine Sessions in diesem Zeitraum." />
        )}
      </Card>
    </div>
  );
}
